import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import ItemRow from "../ItemRow";

function SectionLabel({ label }) {
    return <p className="font-body text-xs uppercase tracking-widest text-warm-500 mt-2 mb-1">{label}</p>;
}

// Day-detail panel under the calendar grid: the selected date's tasks,
// habits (with history view for past days), and reminders, plus the
// "Add for this day" affordance for today/future dates.
export default function CalendarDayDetail({
    selDay, todayKey, onAddForDate,
    selTasks, selHabits, selReminders,
    onToggleTask, onDeleteTask, onEditTaskPriority, onOpenEditTask,
    onToggleHabit, onDeleteHabit, onOpenEditHabit,
    onToggleReminder, onDeleteReminder, onEditReminder, onOpenEditReminder,
}) {
    return (
        <>
            <div className="flex items-center justify-between gap-2 mb-2">
                <p className="font-display text-xs uppercase tracking-widest text-warm-500" data-testid="cal-selected-label">
                    {new Date(selDay + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                </p>
                {onAddForDate && selDay >= todayKey && (
                    <button
                        onClick={() => onAddForDate(selDay)}
                        className="bg-terracotta hover:bg-terracotta-hover text-cream font-display text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1 transition-all active:scale-95"
                        data-testid="cal-add-for-date"
                    >
                        <Plus size={12} /> Add for this day
                    </button>
                )}
            </div>
            <AnimatePresence mode="wait">
                <motion.div
                    key={selDay}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="space-y-2.5"
                >
                    {selTasks.length === 0 && selReminders.length === 0 && selHabits.length === 0 && (
                        <div className="text-center py-6 px-4 bg-surface/50 rounded-2xl border border-dashed border-warm-300/40">
                            <p className="font-body text-sm text-warm-500">Nothing scheduled — a soft day ☁️</p>
                        </div>
                    )}

                    {selTasks.length > 0 && <SectionLabel label="Tasks" />}
                    {selTasks.map((t) => (
                        <ItemRow
                            key={t.id}
                            title={t.title}
                            completed={t.completed}
                            priority={t.priority}
                            time={t.due_time}
                            onToggle={() => onToggleTask(t)}
                            onDelete={() => onDeleteTask(t)}
                            onEditPriority={(next) => onEditTaskPriority(t, next)}
                            onOpenEdit={onOpenEditTask ? () => onOpenEditTask(t) : undefined}
                            testId={`cal-task-${t.id}`}
                        />
                    ))}

                    {selHabits.length > 0 && <SectionLabel label={`Habits${selDay !== todayKey ? " (history)" : ""}`} />}
                    {selHabits.map((h) => (
                        <ItemRow
                            key={h.id}
                            title={h.title}
                            completed={h.completed}
                            time={h.time}
                            streak={h.streak || 0}
                            onToggle={selDay === todayKey ? () => onToggleHabit(h) : undefined}
                            onDelete={selDay >= todayKey ? () => onDeleteHabit(h) : undefined}
                            onOpenEdit={selDay >= todayKey && onOpenEditHabit ? () => onOpenEditHabit(h) : undefined}
                            testId={`cal-habit-${h.id}`}
                        />
                    ))}

                    {selReminders.length > 0 && <SectionLabel label="Reminders" />}
                    {selReminders.map((r) => {
                        // Only expose the pencil menu for reminders whose day is today
                        // or in the future — past reminders are read-only history.
                        const isFuture = selDay >= todayKey;
                        const editable = isFuture && !r.completed;
                        return (
                            <ItemRow
                                key={r.id}
                                title={r.title}
                                completed={r.completed}
                                time={r.time}
                                showCountdown={selDay === todayKey}
                                onToggle={() => onToggleReminder(r)}
                                onDelete={() => onDeleteReminder(r)}
                                onEditTitle={editable ? (next) => onEditReminder(r, { title: next }) : undefined}
                                onEditTime={editable ? (next) => onEditReminder(r, { time: next }) : undefined}
                                onEditLead={editable ? (next) => onEditReminder(r, { lead_minutes: next }) : undefined}
                                onOpenEdit={editable && onOpenEditReminder ? () => onOpenEditReminder(r) : undefined}
                                currentLead={r.lead_minutes}
                                testId={`cal-reminder-${r.id}`}
                            />
                        );
                    })}
                </motion.div>
            </AnimatePresence>
        </>
    );
}
